import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList
} from "recharts";
import { BASE_URL } from "../../../config";
import PeriodFilterBar, { resolvePeriodRange, defaultPeriodFilterValue } from "../filters/PeriodFilterBar";

// Same two colors as the Workers By Positions chart's own bars.
const TOTAL_COLOR = "#00C445";
const ASSIGNED_COLOR = "#1A73E8";

// Below this pixel width, a white "insideRight" label doesn't fit inside the bar and becomes
// invisible -- render it outside the bar in a dark, readable color instead. Same technique
// used by OrderDrilldownFlow's chart for visual consistency across the dashboard's popups.
const MIN_INSIDE_LABEL_WIDTH = 24;
const renderBarCountLabel = ({ x, y, width, height, value }) => {
  const insideFits = width >= MIN_INSIDE_LABEL_WIDTH;
  return (
    <text
      x={insideFits ? x + width - 6 : x + width + 6}
      y={y + height / 2}
      dy={4}
      textAnchor={insideFits ? "end" : "start"}
      fontSize={12}
      fontWeight={600}
      fill={insideFits ? "#ffffff" : "#374151"}
    >
      {value}
    </text>
  );
};

/**
 * 2-level drill-down (division -> client) opened by clicking a bar on the Workers By Positions
 * chart. Visually mirrors OrderDrilldownFlow's modal/back-nav/chart conventions for consistency
 * with the rest of the dashboard's popups, but is purpose-built rather than reused: each row shows
 * Total Shifts vs Assigned Shifts (paired bars) for the position, not shift fill-status, and stops
 * at two levels -- client bars aren't clickable, there's no third detail-list screen.
 */
export default function WorkerPositionDrilldown({ position, onClose, getAuthHeaders, initialPeriodFilter }) {
  const [periodFilter, setPeriodFilter] = useState(initialPeriodFilter || defaultPeriodFilterValue());
  const periodRange = resolvePeriodRange(periodFilter);

  const [level, setLevel] = useState("divisions"); // 'divisions' | 'clients'
  const [selectedDivision, setSelectedDivision] = useState(null); // { id, name }

  const [divisionRows, setDivisionRows] = useState([]);
  const [clientRows, setClientRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const authHeaders = useMemo(() => ({ "Content-Type": "application/json", ...getAuthHeaders() }), [getAuthHeaders]);

  // Level 1: divisions with workers in this position
  useEffect(() => {
    if (level !== "divisions") return;
    let ignore = false;
    setLoading(true);
    const params = new URLSearchParams({ position, startDate: periodRange.startDate, endDate: periodRange.endDate });
    fetch(`${BASE_URL}/api/dashboard/workers-by-skills/divisions?${params}`, { credentials: "include", headers: authHeaders })
      .then((res) => res.json())
      .then((data) => { if (!ignore) setDivisionRows(data.success ? data.data : []); })
      .catch(() => { if (!ignore) setDivisionRows([]); })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [level, position, periodRange.startDate, periodRange.endDate, authHeaders]);

  // Level 2: clients within the selected division
  useEffect(() => {
    if (level !== "clients" || !selectedDivision) return;
    let ignore = false;
    setLoading(true);
    const params = new URLSearchParams({ position, division_id: selectedDivision.id, startDate: periodRange.startDate, endDate: periodRange.endDate });
    fetch(`${BASE_URL}/api/dashboard/workers-by-skills/clients?${params}`, { credentials: "include", headers: authHeaders })
      .then((res) => res.json())
      .then((data) => { if (!ignore) setClientRows(data.success ? data.data : []); })
      .catch(() => { if (!ignore) setClientRows([]); })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [level, position, selectedDivision, periodRange.startDate, periodRange.endDate, authHeaders]);

  const goToDivisions = () => {
    setLevel("divisions");
    setSelectedDivision(null);
  };
  const openDivision = (row) => {
    setSelectedDivision({ id: row.divisionId, name: row.divisionName });
    setLevel("clients");
  };

  const backLink = level === "divisions"
    ? { text: "Back to Dashboard", onClick: onClose }
    : { text: `Back to Divisions with ${position} Shifts`, onClick: goToDivisions };

  const title = level === "divisions"
    ? `Divisions with ${position} Shifts`
    : `${selectedDivision?.name || ""} Shifts`;

  const subtitle = level === "divisions" ? "Division-wise shifts" : "Client-wise shifts";

  const chartRows = level === "divisions"
    ? divisionRows.map((r) => ({ name: r.divisionName, totalShifts: r.totalShifts, assignedShifts: r.assignedShifts, onClick: () => openDivision(r) }))
    : clientRows.map((r) => ({ name: r.clientName, totalShifts: r.totalShifts, assignedShifts: r.assignedShifts }));

  const clickable = level === "divisions";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[92vh] overflow-y-auto p-6 flex flex-col">
        <div className="flex items-center justify-between pb-3 border-b border-gray-200">
          <button type="button" onClick={backLink.onClick} className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700">
            &larr; {backLink.text}
          </button>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 pt-4 pb-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
          </div>
          <PeriodFilterBar value={periodFilter} onChange={setPeriodFilter} showDivisionFilter={false} />
        </div>

        <div className="mt-4 border border-gray-200 rounded-lg p-4 flex-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-medium text-gray-500">{subtitle}</h3>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: TOTAL_COLOR }}></span> Total Shifts
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: ASSIGNED_COLOR }}></span> Assigned Shifts
              </div>
            </div>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-16 text-sm text-gray-400">Loading...</div>
          ) : chartRows.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-sm text-gray-400">No shifts found for the selected period.</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(220, chartRows.length * 60)}>
              <BarChart data={chartRows} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }} barSize={12} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                <XAxis type="number" axisLine={{ stroke: '#e5e7eb' }} tickLine={false} tick={{ fontSize: 11, fill: '#6b7280' }} />
                <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#374151' }} width={140} />
                <Tooltip />
                <Bar
                  dataKey="totalShifts"
                  name="Total Shifts"
                  fill={TOTAL_COLOR}
                  radius={[4, 4, 4, 4]}
                  onClick={(data) => { if (clickable) (data?.payload?.onClick || data?.onClick)?.(); }}
                  cursor={clickable ? "pointer" : "default"}
                >
                  <LabelList dataKey="totalShifts" content={renderBarCountLabel} />
                </Bar>
                <Bar
                  dataKey="assignedShifts"
                  name="Assigned Shifts"
                  fill={ASSIGNED_COLOR}
                  radius={[4, 4, 4, 4]}
                  onClick={(data) => { if (clickable) (data?.payload?.onClick || data?.onClick)?.(); }}
                  cursor={clickable ? "pointer" : "default"}
                >
                  <LabelList dataKey="assignedShifts" content={renderBarCountLabel} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
